(() => {
  'use strict';

  if (customElements.get('snx-creator-marquee')) return;

  class SnxCreatorMarquee extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.initializeFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.viewport = this.querySelector('.snx-creators__viewport');
      this.track = this.querySelector('.snx-creators__track');
      this.original = this.querySelector('.snx-creators__group[data-original]');
      if (!this.viewport || !this.track || !this.original) return;

      this.cards = Array.from(this.original.querySelectorAll('.snx-creators__card'));
      if (!this.cards.length) return;
      this.controller = new AbortController();
      this.pauses = new Set();
      this.ready = false;
      this.offset = 0;
      this.cycleWidth = 0;
      this.lastTime = null;
      this.frame = null;
      this.pointer = null;
      this.suppressClickUntil = 0;
      this.keyboard = false;
      this.keyboardFocus = false;
      this.speed = Math.max(0, Number(this.dataset.speed) || 35);
      this.cards.forEach((card, index) => {
        card.dataset.creatorIndex = String(index);
        card.draggable = false;
      });
      this.original.querySelectorAll('img').forEach(image => { image.draggable = false; });

      this.listen(document, 'keydown', () => { this.keyboard = true; });
      this.listen(document, 'pointerdown', () => {
        this.keyboard = false;
        this.keyboardFocus = false;
      }, { capture: true });
      this.listen(this.viewport, 'focusin', event => {
        if (!this.keyboard && !event.target.matches(':focus-visible')) return;
        this.keyboardFocus = true;
        this.revealCard(event.target.closest('.snx-creators__card'));
      });
      this.listen(this.viewport, 'focusout', event => {
        if (this.viewport.contains(event.relatedTarget)) return;
        this.keyboardFocus = false;
      });
      this.listen(this.viewport, 'keydown', event => this.onKeydown(event));
      this.listen(this.viewport, 'pointerdown', event => this.startDrag(event));
      this.listen(this.viewport, 'pointermove', event => this.moveDrag(event), { passive: false });
      this.listen(window, 'pointerup', event => this.finishDrag(event));
      this.listen(window, 'pointercancel', event => this.finishDrag(event));
      this.listen(this.viewport, 'lostpointercapture', event => this.finishDrag(event));
      this.listen(this.viewport, 'dragstart', event => event.preventDefault());
      this.listen(this.viewport, 'click', event => {
        if (performance.now() >= this.suppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClickUntil = 0;
      }, { capture: true });
      this.listen(this.viewport, 'scroll', () => {
        if (this.ready && this.viewport.scrollLeft) this.viewport.scrollLeft = 0;
      });
      this.listen(document, 'visibilitychange', () => this.setPause('hidden', document.hidden));

      this.setPause('hidden', document.hidden);
      if ('IntersectionObserver' in window) {
        this.setPause('offscreen', true);
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.setPause('offscreen', !entries[0].isIntersecting);
        });
        this.intersectionObserver.observe(this.viewport);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.rebuild());
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.original);
      } else {
        this.listen(window, 'resize', () => this.rebuild());
      }
      this.rebuild();
    }

    listen(target, type, listener, options = {}) {
      target.addEventListener(type, listener, { ...options, signal: this.controller.signal });
    }

    rebuild() {
      if (!this.isConnected || !this.controller) return;
      const originalWidth = this.original.getBoundingClientRect().width;
      const viewportWidth = this.viewport.clientWidth;
      if (!originalWidth || !viewportWidth) return;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap) || parseFloat(styles.getPropertyValue('--snx-creators-gap')) || 0;
      const nextCycle = originalWidth + gap;
      const progress = this.cycleWidth ? this.offset / this.cycleWidth : 0;
      const focused = document.activeElement;
      const focusedCard = this.track.contains(focused) ? focused.closest('.snx-creators__card') : null;
      const focusedIndex = focusedCard ? Number(focusedCard.dataset.creatorIndex) : -1;
      const focusedClone = focusedCard && !this.original.contains(focusedCard);
      const count = Math.ceil(viewportWidth / nextCycle) + 2;
      const clones = Array.from(this.track.querySelectorAll('[data-creators-clone]'));

      while (clones.length > count - 1) clones.pop().remove();
      while (clones.length < count - 1) {
        const clone = this.original.cloneNode(true);
        clone.removeAttribute('data-original');
        clone.removeAttribute('id');
        clone.setAttribute('data-creators-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(element => {
          element.tabIndex = -1;
        });
        this.track.append(clone);
        clones.push(clone);
      }

      this.cycleWidth = nextCycle;
      this.offset = progress * this.cycleWidth;
      if (this.pointer) {
        this.pointer.offset = this.offset;
        this.pointer.x = this.pointer.lastX;
        this.pointer.y = this.pointer.lastY;
      }
      this.ready = true;
      this.classList.add('is-ready');
      this.paint();
      if (focusedClone && !focusedCard.isConnected && this.cards[focusedIndex]) {
        this.cards[focusedIndex].focus({ preventScroll: true });
      }
      if (this.keyboardFocus && focusedIndex >= 0) this.revealCard(this.cards[focusedIndex]);
      this.updatePlayback();
    }

    paint() {
      if (!this.cycleWidth) return;
      this.offset = ((this.offset % this.cycleWidth) + this.cycleWidth) % this.cycleWidth;
      this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
    }

    setPause(reason, paused) {
      if (paused) this.pauses.add(reason);
      else this.pauses.delete(reason);
      this.updatePlayback();
    }

    updatePlayback() {
      if (this.ready && !this.pauses.size && this.isConnected) {
        if (this.frame === null) this.frame = requestAnimationFrame(time => this.tick(time));
      } else {
        if (this.frame !== null) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.lastTime = null;
      }
    }

    tick(time) {
      this.frame = null;
      if (!this.controller || !this.ready || this.pauses.size || !this.isConnected) return;
      const elapsed = this.lastTime === null ? 0 : Math.min(time - this.lastTime, 50) / 1000;
      this.lastTime = time;
      this.offset += this.speed * elapsed;
      this.paint();
      this.frame = requestAnimationFrame(next => this.tick(next));
    }

    revealCard(card) {
      if (!card || !this.ready) return;
      const originalCard = this.cards[Number(card.dataset.creatorIndex)];
      if (!originalCard) return;
      const groupBounds = this.original.getBoundingClientRect();
      const bounds = originalCard.getBoundingClientRect();
      const left = bounds.left - groupBounds.left;
      const right = left + bounds.width;
      const width = this.viewport.clientWidth;
      if (left < this.offset || bounds.width > width) this.offset = left;
      else if (right > this.offset + width) this.offset = right - width;
      this.offset = Math.max(0, Math.min(this.offset, Math.max(0, groupBounds.width - width)));
      this.viewport.scrollLeft = 0;
      this.paint();
    }

    onKeydown(event) {
      if (!this.ready) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const card = event.target.closest('.snx-creators__card');
      const current = card ? Number(card.dataset.creatorIndex) : Math.floor(this.offset / (this.cycleWidth / this.cards.length));
      const index = (current + direction + this.cards.length) % this.cards.length;
      this.keyboard = true;
      this.keyboardFocus = true;
      this.cards[index].focus({ preventScroll: true });
      this.revealCard(this.cards[index]);
    }

    startDrag(event) {
      if (!this.ready || this.pointer || !event.isPrimary || event.button !== 0) return;
      this.pointer = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        offset: this.offset,
        dragging: false
      };
    }

    moveDrag(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      this.pointer.lastX = event.clientX;
      this.pointer.lastY = event.clientY;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      if (!this.pointer.dragging && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
        this.finishDrag(event);
        return;
      }
      if (!this.pointer.dragging && Math.abs(dx) > 6) {
        this.pointer.dragging = true;
        this.pointer.offset = this.offset;
        this.setPause('drag', true);
        this.viewport.setPointerCapture(event.pointerId);
        this.viewport.classList.add('is-dragging');
      }
      if (!this.pointer.dragging) return;
      if (event.cancelable) event.preventDefault();
      this.offset = this.pointer.offset - dx;
      this.paint();
    }

    finishDrag(event) {
      if (!this.pointer || (event && event.pointerId !== this.pointer.id)) return;
      const pointer = this.pointer;
      this.pointer = null;
      if (pointer.dragging) this.suppressClickUntil = performance.now() + 450;
      if (this.viewport.hasPointerCapture(pointer.id)) this.viewport.releasePointerCapture(pointer.id);
      this.viewport.classList.remove('is-dragging');
      this.setPause('drag', false);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initializeFrame);
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.pointer && this.viewport.hasPointerCapture(this.pointer.id)) {
        this.viewport.releasePointerCapture(this.pointer.id);
      }
      this.pointer = null;
      this.ready = false;
      this.track.querySelectorAll('[data-creators-clone]').forEach(clone => clone.remove());
      this.track.style.removeProperty('transform');
      this.viewport.classList.remove('is-dragging');
      this.classList.remove('is-ready');
    }
  }

  customElements.define('snx-creator-marquee', SnxCreatorMarquee);
})();
